import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaWhatsapp, FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaCheck, FaTimes, FaBus, FaClock } from 'react-icons/fa';
import BookingForm from '../../components/common/BookingForm';
import { companyInfo } from '../../data/tourData';
import './KedarnathTour.css';

const KedarnathTour = () => {
  return (
    <>
      <Helmet>
        <title>Kedarnath Tour - NomadTrailz</title>
        <meta name="description" content="Experience the sacred Kedarnath pilgrimage with NomadTrailz. Trek to the highest Shiva temple at 11,759 feet." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Hero Section */}
        <section className="destination-hero">
          <div className="hero-background">
            <img 
              src="https://res.cloudinary.com/duym2bgp2/image/upload/v1755008833/o90y9z1p2kvbmuitn1jx.jpg" 
              alt="Kedarnath" 
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Kedarnath Tour
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Sacred pilgrimage to the highest Shiva temple at 11,759 feet
            </motion.p>
          </div>
        </section>

        {/* Tour Details */}
        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div 
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>
                    Kedarnath is one of the most sacred pilgrimage sites in India, located in the Rudraprayag district 
                    of Uttarakhand. This spiritual journey takes you to the highest Shiva temple at 11,759 feet, 
                    where Lord Shiva is worshipped in the form of a hump. The trek offers breathtaking views of 
                    snow-capped peaks and pristine landscapes, making it a perfect blend of spirituality and adventure.
                  </p>
                </motion.div>

                <motion.div 
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Sacred Kedarnath Temple at 11,759 feet</li>
                    <li>Spiritual darshan of Lord Shiva</li>
                    <li>Scenic trek through beautiful valleys</li>
                    <li>Snow-capped mountain views</li>
                    <li>Devprayag Sangam (confluence of rivers)</li>
                    <li>Ardh Nareshwar Temple visit</li>
                    <li>Rishikesh exploration</li>
                    <li>Challenging high altitude trekking</li>
                  </ul>
                </motion.div>

                <motion.div 
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: Transfer To Guptkashi</h4>
                    <ul>
                      <li>Overnight departure from Delhi at 9 PM</li>
                      <li>Pick up from predetermined location</li>
                      <li>Scenic drive through Devprayag</li>
                      <li>Capture the confluence of Bhagirathi and Alaknanda rivers</li>
                      <li>Drive alongside Mandakani River from Tilwara</li>
                      <li>Visit Ardh Nareshwar Temple</li>
                      <li>Arrive in Guptkashi, lunch and check-in</li>
                      <li>Freshen up, dinner and overnight stay</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: Trek To Kedarnath</h4>
                    <ul>
                      <li>Depart around 2-3 AM to avoid crowds</li>
                      <li>Travel to Sonprayag</li>
                      <li>Take local jeep to Gaurikund</li>
                      <li>Begin trek to Kedarnath</li>
                      <li>Pass through beautiful valleys with snow-capped mountains</li>
                      <li>Experience frosty trees and stunning landscapes</li>
                      <li>Arrive in Kedarnath</li>
                      <li>Feel the spiritual ambiance with prayers and bells</li>
                      <li>Overnight stay in Kedarnath</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: Kedarnath Darshan | Trek Down</h4>
                    <ul>
                      <li>Early morning mesmerizing sunrise</li>
                      <li>Kedarnath Darshan</li>
                      <li>Seek blessings of Lord Shiva at 11,759 feet</li>
                      <li>Begin descent to Gaurikund</li>
                      <li>Return to Guptkashi in the evening</li>
                      <li>Free evening for leisure</li>
                      <li>Group dinner and overnight stay in Guptkashi</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 4: Departure via Rishikesh</h4>
                    <ul>
                      <li>Hearty breakfast and check out</li>
                      <li>Head towards Rishikesh</li>
                      <li>Spend 1-2 hours exploring vibrant markets</li>
                      <li>Visit Ram Jhula and Laxman Jhula</li>
                      <li>Relax by the Ganga River</li>
                      <li>Begin journey back to Delhi in afternoon</li>
                      <li>Reach Delhi next morning with unforgettable memories</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div 
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li><FaCheck className="check-icon" />Accommodation (Basic Homestay & Camps)</li>
                    <li><FaCheck className="check-icon" />Meals (3 Breakfast, 3 lunch and 3 Dinner)</li>
                    <li><FaCheck className="check-icon" />Trek Guide, Driver, Road tax, Vehicle Parking</li>
                    <li><FaCheck className="check-icon" />Transportation in Tempo traveller</li>
                    <li><FaCheck className="check-icon" />Equipment's for climbing (if required)</li>
                  </ul>
                </motion.div>

                <motion.div 
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li><FaTimes className="times-icon" />Any personal Expenses / Adventure activities</li>
                    <li><FaTimes className="times-icon" />Anything not mentioned in the itinerary</li>
                    <li><FaTimes className="times-icon" />Any kind of entry tickets/fees</li>
                    <li><FaTimes className="times-icon" />Any Meals / Drinks other than Inclusion</li>
                    <li><FaTimes className="times-icon" />5%GST</li>
                    <li><FaTimes className="times-icon" />Personal Porter</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div 
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">₹ 7,999</span>
                    <span className="duration">4 Days / 3 Nights</span>
                  </div>
                  
                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>Kedarnath, Uttarakhand</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: May to October</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: Challenging</span>
                    </div>
                  </div>

                  <div className="pricing-options">
                    <h4>Cost Per Person</h4>
                    <div className="price-option">
                      <span className="option">TRIPLE SHARE</span>
                      <span className="amount">₹ 7,999 + 5% GST</span>
                    </div>
                    <div className="price-option">
                      <span className="option">DOUBLE SHARE</span>
                      <span className="amount">₹ 8,999 + 5% GST</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div 
                  className="tour-logistics-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  <h3>Tour Logistics</h3>
                  <div className="logistics-grid">
                    <div className="logistics-item">
                      <FaBus className="logistics-icon" />
                      <div className="logistics-content">
                        <span className="logistics-label">Pickup Point:</span>
                        <span className="logistics-value">Delhi: ISBT Kashmere Gate</span>
                      </div>
                    </div>
                    <div className="logistics-item">
                      <FaClock className="logistics-icon" />
                      <div className="logistics-content">
                        <span className="logistics-label">Pickup Time:</span>
                        <span className="logistics-value">10:00 PM</span>
                      </div>
                    </div>
                    <div className="logistics-item">
                      <FaMapMarkerAlt className="logistics-icon" />
                      <div className="logistics-content">
                        <span className="logistics-label">Maximum Altitude:</span>
                        <span className="logistics-value">11,759 feet</span>
                      </div>
                    </div>
                    <div className="logistics-item">
                      <FaStar className="logistics-icon" />
                      <div className="logistics-content">
                        <span className="logistics-label">Trek Distance:</span>
                        <span className="logistics-value">Approx. 16 km</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName="Kedarnath Sacred Pilgrimage"
                  tourPrice="₹ 7,999"
                  tourDuration="4 Days / 3 Nights"
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default KedarnathTour;
